import React, { useState, useContext } from 'react';
import { graphql, Link } from 'gatsby';
import Layout from '../components/Global/Layout/Layout';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import { StoreContext } from '../context/StoreContext';
import { Row, Col } from 'react-bootstrap';
import styles from '../components/Page-CSS/ProductDetailTemplatePage.module.css';
import { RiArrowRightSLine } from 'react-icons/ri';
import Img from 'gatsby-image';
import LightBox from '../components/Global/LightBox/LightBox';
import SizeGuide from '../components/Global/SizeGuide/SizeGuide';
import SlideShow from '../components/Global/SlideShow/SlideShow';
import SEO from '../components/Global/SEO';
import { v4 as uuidv4 } from 'uuid';

const ProductDetailTemplate = ({ data }) => {
	const { shopifyProduct: product } = data;
	const { nodes } = data.allShopifyCollection;

	const { options, variants, variants: [ initialVariant ] } = product;
	const [ variant, setVariant ] = useState({ ...initialVariant });
	const [ image, setImage ] = useState({ ...initialVariant });
	const [ descriptionIsShown, setDescriptionIsShown ] = useState(false);
	const [ modalShow, setModalShow ] = useState(false);
	const [ sizeGuideShow, setSizeGuideShow ] = useState(false);

	const { client, quantity, setQuantity, addProductToCart } = useContext(StoreContext);

	const productVariant = client.product.helpers.variantForOptions(product, variant) || variant;

	const handleClick = (optionIndex, value) => {
		const currentOptions = [ ...variant.selectedOptions ];

		currentOptions[optionIndex] = {
			...currentOptions[optionIndex],
			value
		};

		const selectedVariant = find(variants, ({ selectedOptions }) => isEqual(currentOptions, selectedOptions));

		setVariant({ ...selectedVariant });

		setImage({ ...selectedVariant });
	};

	const displayLightBox = (variants) => {
		const colorVariant = variants.filter((variant) => variant.name === 'Color');
		const result = colorVariant[0].values.length === 1 ? false : true;
		return result;
	};

	const currentCollection = nodes.filter((collection) => collection.handle === product.productType);
	const collectionAfterCurrentProduct = currentCollection[0].products.filter((item) => item.title !== product.title);

	return (
		<Layout>
			<SEO
				title={product.title}
				description={product.description}
				image={image.image.localFile.childImageSharp.fluid.src}
			/>
			<Row className={styles.product}>
				<Col xs={12} md={6} lg={5}>
					{image ? (
						<div onClick={() => setModalShow(true)}>
							<Img
								fluid={image.image.localFile.childImageSharp.fluid}
								alt={`${product.title} in ${image.title}`}
							/>
						</div>
					) : null}

					{displayLightBox(data.shopifyProduct.options) && (
						<Row className={styles.thumbnailHolder}>
							<LightBox
								show={modalShow}
								onHide={() => setModalShow(false)}
								images={data.shopifyProduct.images}
							/>

							<Col xs={3} className={styles.thumbnails} onClick={() => setModalShow(true)}>
								<Img fluid={data.shopifyProduct.images[0].localFile.childImageSharp.fluid} alt="alt" />
							</Col>

							{data.shopifyProduct.images[1] ? (
								<Col xs={3} className={styles.thumbnails} onClick={() => setModalShow(true)}>
									<Img
										fluid={data.shopifyProduct.images[1].localFile.childImageSharp.fluid}
										alt="alt"
									/>
								</Col>
							) : null}

							<Col xs={3} className={styles.thumbnailCta} onClick={() => setModalShow(true)}>
								<p className={styles.thumbnailCtaP}>
									See Full Gallery <RiArrowRightSLine />
								</p>
							</Col>
						</Row>
					)}
				</Col>
				<Col xs={12} md={6} lg={5} className={styles.productDetails}>
					<h1 className={styles.productTitle}>{product.title}</h1>
					<h4 className={styles.price}>£ {variant.price}</h4>

					<Row>
						{options.map(({ id, name, values }, optionIndex) => (
							<Col xs={12} className={styles.variantHolder} key={uuidv4()}>
								<p className={styles.variantName}>{name}</p>

								<div style={{ display: 'flex' }}>
									{values.map((value) => (
										<button
											key={uuidv4()}
											className={`${styles.variantBtn} ${variant.title.includes(value)
												? styles.active
												: ''}`}
											onClick={() => handleClick(optionIndex, value)}
										>
											{value}
										</button>
									))}
								</div>
							</Col>
						))}

						<Col xs={12}>
							<p
								onClick={() => setDescriptionIsShown(!descriptionIsShown)}
								className={styles.description}
							>
								Description <RiArrowRightSLine />
							</p>
							{descriptionIsShown && <p className={styles.variantDescription}>{product.description}</p>}
						</Col>

						<Col xs={12}>
							<p onClick={() => setSizeGuideShow(true)} className={styles.sizeGuide}>
								Size Guide <RiArrowRightSLine />
							</p>
							<SizeGuide show={sizeGuideShow} onHide={() => setSizeGuideShow(false)} />
						</Col>

						<Col xs={12} className={styles.inputHolder}>
							<label htmlFor="quantity">Quantity of this item</label>
							<input
								id="quantity"
								type="number"
								value={quantity}
								onChange={(e) => setQuantity(e.target.value)}
								className={styles.quantityInput}
								min="1"
							/>
						</Col>

						<Col xs={12}>
							<button className={styles.btn} onClick={() => addProductToCart(productVariant.shopifyId)}>
								Add To Cart
							</button>
						</Col>
						<Col xs={6}>
							<Link className={styles.link} to="/faqs/">
								Delivery Details
							</Link>
						</Col>
						<Col xs={6} className={styles.returns}>
							<Link className={styles.link} to="/faqs/">
								Our Return Policy
							</Link>
						</Col>
					</Row>
				</Col>

				<Col xs={12} className={styles.slideShow}>
					<SlideShow
						products={collectionAfterCurrentProduct}
						title="YOU MIGHT ALSO LIKE"
						specialHandle={`collections/${currentCollection[0].handle}`}
					/>
				</Col>
			</Row>
		</Layout>
	);
};

export default ProductDetailTemplate;

export const query = graphql`
	query($handle: String!) {
		shopifyProduct(handle: { eq: $handle }) {
			id
			handle
			title
			productType
			description
			options {
				id
				name
				values
			}
			variants {
				id
				title
				price
				availableForSale
				shopifyId
				image {
					localFile {
						childImageSharp {
							fluid {
								...GatsbyImageSharpFluid
							}
						}
					}
				}
				selectedOptions {
					name
					value
				}
			}
			images {
				id
				localFile {
					childImageSharp {
						fluid(maxWidth: 800, maxHeight: 800) {
							...GatsbyImageSharpFluid
						}
					}
				}
			}
		}
		allShopifyCollection {
			nodes {
				title
				handle
				products {
					title
					priceRange {
						minVariantPrice {
							amount
						}
					}
					images {
						localFile {
							childImageSharp {
								fluid {
									...GatsbyImageSharpFluid
								}
							}
						}
					}
					handle
				}
			}
		}
	}
`;
